import { ApolloQueryResult } from '@apollo/client';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { ReactElement } from 'react';
import { useStoryblok } from 'storyblok';

import { Layout } from '@/components';
import Footer from '@/components/global/Footer';
import Header from '@/components/global/Header';
import BaseSeo from '@/components/seo/BaseSeo';
import Proofer from '@/components/tools/Proofer/Proofer';
import { serverApolloClient } from '@/lib/auth/useApolloClient';
import { VERSION } from '@/lib/constants';
import { contextToRegionQuery } from '@/lib/regions';
import { getStoryBlokProps } from '@/lib/storyblok/storyblokGetStaticProps';
import { getStoryBlokNavLinks } from '@/lib/storyblok/storyblokNavLinks';
import {
  CategoryBySlugDocument,
  CategoryBySlugQuery,
  CategoryBySlugQueryVariables,
  ProductCollectionDocument,
  ProductCollectionQuery,
  ProductCollectionQueryVariables
} from '@/saleor/api';

const ToolsPage = ({
  story,
  preview,
  resolve_relations,
  products,
  categoryId
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const sbStory = preview ? useStoryblok(story, preview, resolve_relations) : story;
  const globalLinks = getStoryBlokNavLinks(story);

  const {
    content: { SEO = {} },
    name,
    full_slug
  } = sbStory;

  return (
    <div>
      <BaseSeo
        title={SEO?.title}
        description={SEO?.description}
        ogDescription={SEO?.og_description}
        ogTitle={SEO?.og_title}
        image={SEO?.og_image}
        path={full_slug}
      />
      <Header navigation={globalLinks} headerTheme="header-white" pageName={name} hasBorder />
      <main id="main" className="page-padding-top page-theme-charcoal th-white-charcoal">
        <Proofer serverFontFamilies={{ products: products }} categoryId={categoryId} />
      </main>
      <Footer theme="charcoal" navigation={globalLinks.footerLinks} />
    </div>
  );
};

export default ToolsPage;

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const pageSlug = 'tools/proofer';
  const preview = context.preview || false;
  const resolveRelations = 'PageToolsProofer.globalHeader,PageToolsProofer.globalFooter';

  const storyPromise = await getStoryBlokProps({
    pageSlug,
    context,
    VERSION,
    resolveRelations
  });

  const pageData = await Promise.all([storyPromise]).then((values) => values);

  const story = pageData[0];

  if (story?.notFound) return story;

  const response: ApolloQueryResult<CategoryBySlugQuery> = await serverApolloClient.query<
    CategoryBySlugQuery,
    CategoryBySlugQueryVariables
  >({
    query: CategoryBySlugDocument,
    variables: {
      slug: 'typefaces',
      locale: contextToRegionQuery(context).locale
    }
  });

  const { id: categoryId } = response.data.category;

  const productsRes: ApolloQueryResult<ProductCollectionQuery> = await serverApolloClient.query<
    ProductCollectionQuery,
    ProductCollectionQueryVariables
  >({
    query: ProductCollectionDocument,
    variables: {
      first: 7,
      filter: { categories: [categoryId] },
      channel: 'uk-store',
      locale: contextToRegionQuery(context).locale
    }
  });

  const products = productsRes?.data?.products;

  return {
    props: {
      preview,
      story: storyPromise || null,
      resolve_relations: resolveRelations,
      products: products,
      categoryId
    },
    revalidate: 60 * 60 // value in seconds, how often ISR will trigger on the server
  };
};

ToolsPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
